import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Bounce, toast } from 'react-toastify';
import Select from 'react-select';
import { components as selectComponents } from 'react-select';
import CustomButton from '../OnboardingComponents/CustomButton';
import { Avatar } from './AvatarGroup';
import { ICONS } from '../../constants/icons';
import { get, post } from '../../api/axios';
import endpoints from '../../api/endpoints';
import { toastApiError } from '../../helpers/apiError';
import { getUserAvatarUrl } from '../../helpers/userAvatar';

const mapMemberToOption = (member) => {
  const user = member?.user || {};
  const userId = member?.user_id || user.id || member?.userId || '';
  const name =
    member?.name ||
    `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
    user.full_name ||
    user.email ||
    'Collaborator';
  const email = user.email || member?.email || '';

  return {
    value: userId ? String(userId) : '',
    memberId: member?.id ? String(member.id) : '',
    label: email ? `${name} (${email})` : name,
    name,
    email,
    avatar: member?.avatar || getUserAvatarUrl(user),
  };
};

const memberKeys = (member) => {
  const user = member?.user || {};
  return [
    member?.id,
    member?.user_id,
    member?.userId,
    user.id,
    user.email,
    member?.email,
  ]
    .filter(Boolean)
    .map((value) => String(value).trim().toLowerCase());
};

const CollaboratorOption = (props) => {
  const { data } = props;
  return (
    <selectComponents.Option {...props}>
      <span className="project-collab-modal__option">
        <Avatar
          src={data.avatar}
          name={data.name}
          size="xs"
          className="project-collab-modal__option-avatar"
        />
        <span className="project-collab-modal__option-meta">
          <span className="project-collab-modal__option-name">{data.name}</span>
          {data.email ? (
            <span className="project-collab-modal__option-email">{data.email}</span>
          ) : null}
        </span>
      </span>
    </selectComponents.Option>
  );
};

const CollaboratorMultiValueLabel = (props) => {
  const { data } = props;
  return (
    <selectComponents.MultiValueLabel {...props}>
      <span className="project-collab-modal__chip">
        <Avatar
          src={data.avatar}
          name={data.name}
          size="xs"
          className="project-collab-modal__chip-avatar"
        />
        <span className="project-collab-modal__chip-name">{data.name}</span>
      </span>
    </selectComponents.MultiValueLabel>
  );
};

const selectStyles = (hasError = false) => ({
  control: (base, state) => ({
    ...base,
    minHeight: 30,
    border: 'none',
    borderRadius: 12,
    backgroundColor: 'transparent',
    boxShadow: 'none',
    cursor: 'text',
    paddingLeft: 0,
    borderColor: hasError ? '#9b0000' : 'transparent',
    '&:hover': { border: 'none', backgroundColor: 'transparent' },
    ...(state.isFocused ? { outline: 'none' } : {}),
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0',
    gap: 6,
    minHeight: 30,
  }),
  container: (base) => ({
    ...base,
    width: '100%',
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    color: '#0d0d0d',
    fontSize: 14,
    fontWeight: 500,
  }),
  placeholder: (base) => ({
    ...base,
    margin: 0,
    color: 'rgba(110, 110, 110, 0.7)',
    fontSize: 14,
    fontWeight: 500,
  }),
  multiValue: (base) => ({
    ...base,
    margin: 0,
    backgroundColor: '#ecf0fa',
    borderRadius: 999,
  }),
  multiValueLabel: (base) => ({
    ...base,
    padding: '2px 4px 2px 2px',
  }),
  multiValueRemove: (base) => ({
    ...base,
    borderRadius: 999,
    color: '#6e6e6e',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: 'rgba(155, 0, 0, 0.08)',
      color: '#9b0000',
    },
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 30,
    alignSelf: 'center',
  }),
  dropdownIndicator: () => ({ display: 'none' }),
  indicatorSeparator: () => ({ display: 'none' }),
  menu: (base) => ({
    ...base,
    marginTop: 8,
    borderRadius: 12,
    boxShadow: '0 8px 24px rgba(17, 17, 17, 0.08)',
    overflow: 'hidden',
    zIndex: 10060,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 10060 }),
  menuList: (base) => ({
    ...base,
    padding: 4,
    maxHeight: 220,
  }),
  option: (base, state) => ({
    ...base,
    padding: '8px 10px',
    borderRadius: 8,
    backgroundColor:
      state.isFocused || state.isSelected ? '#f2f2f4' : 'transparent',
    color: '#0d0d0d',
    cursor: 'pointer',
    '&:active': { backgroundColor: '#e7e7ea' },
  }),
  noOptionsMessage: (base) => ({
    ...base,
    fontSize: 13,
    color: '#b7b7b7',
  }),
  loadingMessage: (base) => ({
    ...base,
    fontSize: 13,
    color: '#b7b7b7',
  }),
});

const TaskAddCollaboratorDropdown = ({
  projectId,
  taskId,
  existingMemberKeys = [],
  onAdded,
}) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);

  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const resetState = useCallback(() => {
    setSelected([]);
    setError('');
    setMenuOpen(false);
  }, []);

  useEffect(() => {
    if (!open || !projectId) return undefined;

    let cancelled = false;

    const loadMembers = async () => {
      setIsLoading(true);
      const response = await get(endpoints.getProjectInvitations, {
        params: {
          involvement: 'all',
          status: 'active',
          project_id: projectId,
          per_page: 100,
          'with[]': ['user'],
        },
        skipErrorToast: true,
      });

      if (cancelled) return;

      if (response?.data?.success) {
        const assigned = new Set(
          existingMemberKeys.map((key) => String(key).trim().toLowerCase())
        );
        const mapped = (response.data.result?.data || [])
          .filter((member) => String(member.status || '').toLowerCase() === 'active')
          .filter((member) => !memberKeys(member).some((key) => assigned.has(key)))
          .map(mapMemberToOption)
          .filter((option) => option.value);
        setOptions(mapped);
      } else {
        setOptions([]);
      }
      setIsLoading(false);
    };

    loadMembers();
    return () => {
      cancelled = true;
    };
  }, [open, projectId, existingMemberKeys]);

  useEffect(() => {
    if (!open) return undefined;

    const close = (event) => {
      if (
        triggerRef.current?.contains(event.target) ||
        panelRef.current?.contains(event.target) ||
        event.target.closest?.('.project-collab-modal-user__menu')
      ) {
        return;
      }
      setOpen(false);
      resetState();
    };

    document.addEventListener('mousedown', close);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('resize', close);
    };
  }, [open, resetState]);

  const toggleMenu = (event) => {
    event.stopPropagation();
    if (open) {
      setOpen(false);
      resetState();
      return;
    }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = 320;
    setCoords({
      top: rect.bottom + 8,
      left: Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8)),
      width,
    });
    setOpen(true);
  };

  const handleSave = async () => {
    if (isSaving) return;
    const userIds = selected.map((item) => item.value).filter(Boolean);
    if (!projectId || !taskId) {
      toast.error('Task is missing. Unable to add collaborators.', {
        position: 'bottom-center',
        theme: 'colored',
        transition: Bounce,
      });
      return;
    }
    if (userIds.length === 0) {
      setError('Select at least one collaborator');
      return;
    }

    setIsSaving(true);
    setError('');
    try {
      const response = await post(
        endpoints.updateTaskCollaborators(projectId, taskId),
        {
          user_ids: userIds,
          action: true,
        }
      );

      if (response?.data?.success === false) {
        throw new Error(response?.data?.message || 'Failed to add collaborators');
      }

      toast.success(response?.data?.message || 'Collaborators added.', {
        position: 'bottom-center',
        theme: 'colored',
        transition: Bounce,
      });
      setOpen(false);
      resetState();
      await onAdded?.(selected);
    } catch (err) {
      toastApiError(err, 'Failed to add collaborators');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="project-task-detail__add-collab"
        onClick={toggleMenu}
        aria-label="Add collaborator"
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <img src={ICONS.plus} alt="" width={12} height={12} />
      </button>
      {open && coords
        ? createPortal(
            <div
              ref={panelRef}
              className="project-task-detail__invite-panel project-collab-modal project-collab-modal--invite"
              role="dialog"
              aria-label="Add collaborator"
              style={{ top: coords.top, left: coords.left, width: coords.width }}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="project-collab-modal__field">
                <label
                  className="project-collab-modal__label"
                  htmlFor="task-collab-member-search"
                >
                  Search Collaborator
                </label>
                <div
                  className={`project-collab-modal__search${
                    error ? ' project-collab-modal__search--error' : ''
                  }`}
                >
                  <img src={ICONS.messageSearchCollab} alt="" />
                  <Select
                    inputId="task-collab-member-search"
                    className="project-collab-modal__user-select"
                    classNamePrefix="project-collab-modal-user"
                    options={options}
                    value={selected}
                    onChange={(next) => {
                      setSelected(next || []);
                      setError('');
                    }}
                    isMulti
                    isSearchable
                    isClearable={false}
                    isDisabled={isSaving}
                    isLoading={isLoading}
                    menuIsOpen={menuOpen}
                    onMenuOpen={() => setMenuOpen(true)}
                    onMenuClose={() => setMenuOpen(false)}
                    closeMenuOnSelect={false}
                    hideSelectedOptions
                    filterOption={(option, rawInput) => {
                      const assigned = new Set(
                        existingMemberKeys.map((key) =>
                          String(key).trim().toLowerCase()
                        )
                      );
                      const data = option.data || {};
                      const keys = [data.value, data.memberId, data.email]
                        .filter(Boolean)
                        .map((value) => String(value).trim().toLowerCase());
                      if (keys.some((key) => assigned.has(key))) return false;
                      const query = String(rawInput || '').trim().toLowerCase();
                      if (!query) return true;
                      const haystack = `${option.label} ${data.email || ''}`.toLowerCase();
                      return haystack.includes(query);
                    }}
                    placeholder="Search project members..."
                    noOptionsMessage={() =>
                      isLoading ? 'Loading...' : 'No active members found'
                    }
                    loadingMessage={() => 'Loading...'}
                    components={{
                      Option: CollaboratorOption,
                      MultiValueLabel: CollaboratorMultiValueLabel,
                      IndicatorSeparator: null,
                      DropdownIndicator: null,
                    }}
                    styles={selectStyles(Boolean(error))}
                    menuPortalTarget={
                      typeof document !== 'undefined' ? document.body : null
                    }
                    menuPosition="fixed"
                    aria-label="Search Collaborator"
                  />
                </div>
                {error ? (
                  <span className="project-collab-modal__error">{error}</span>
                ) : null}
              </div>
              <div className="project-task-detail__invite-actions">
                <CustomButton
                  variant="primary"
                  size="sm"
                  type="button"
                  onClick={handleSave}
                  loading={isSaving}
                  disabled={isSaving || selected.length === 0}
                  fullWidth
                >
                  Save
                </CustomButton>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
};

export default TaskAddCollaboratorDropdown;
